import { Flex, Form, Select } from "antd";
import { useForm } from "antd/es/form/Form";
import { ResetFilterButton } from "pages/schedule/component/ResetFilterButton";
import { getAllClassTypeByBusinessId } from "hooks/queries/classtype";
import { getAllCustomerByBusinessId } from "hooks/queries/customer";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import { memo, useMemo } from "react";
import { translate } from "shared/languages";
import { ClassCategory, UserType } from "shared/types/enum";
import { capitalizeFirstLetter } from "shared/utils";
import { useScheduleState } from "./ScheduleFilterContext";
import { useInstructorColor } from "context/InstructorColorProvider";

const DEFAULT_CLASS_TYPES = [ClassCategory.Private, ClassCategory.Open];

export const ScheduleFilter = memo(() => {
  const [form] = useForm();
  const { state, setFilters } = useScheduleState();
  const {
    instructorIds: initialStaffIds,
    customerIds: initialCustomerIds,
    classIds: initialClassIds,
    classTypes: initialClassTypes,
  } = state.filters;

  const { data: customers, isLoading: customerLoading } =
    getAllCustomerByBusinessId({ is_active: true });
  const { data: staffs, isLoading: staffLoading } = getAllStaffByBusinessId(
    UserType.Instructor,
    { is_active: true }
  );
  const { data: classes, isLoading: classLoading } =
    getAllClassTypeByBusinessId(
      initialClassTypes.length === 1 ? initialClassTypes[0] : undefined
    );

  const customerOptions = useMemo(
    () =>
      customers
        ?.slice()
        .sort((a, b) => a.full_name.localeCompare(b.full_name))
        .map((customer) => ({
          label: customer.full_name,
          value: customer.id,
        })) || [],
    [customers]
  );

  // Each instructor with their calendar colour, so the colours on the
  // calendar mean something without hovering a class. Searched by name.
  const { getInstructorColorById } = useInstructorColor();
  const staffOptions = useMemo(
    () =>
      staffs
        ?.slice()
        .sort((a, b) => a.full_name.localeCompare(b.full_name))
        .map((staff) => ({
          label: (
            <Flex align="center" gap={6} style={{ minWidth: 0 }}>
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  flexShrink: 0,
                  background: getInstructorColorById(staff.id)?.accent,
                }}
              />
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {staff.full_name}
              </span>
            </Flex>
          ),
          name: staff.full_name,
          value: staff.id,
        })) || [],
    [staffs, getInstructorColorById]
  );

  const classOptions = useMemo(() => {
    if (!classes) return [];

    const privateClasses = classes.filter(
      (cls) => cls.type === ClassCategory.Private
    );
    const openClasses = classes.filter(
      (cls) => cls.type === ClassCategory.Open
    );
    const options = [];

    if (privateClasses.length > 0) {
      options.push({
        label: "Private",
        title: "Private",
        options: privateClasses.map((cls) => ({
          label: cls.name,
          value: cls.id,
        })),
      });
    }

    if (openClasses.length > 0) {
      options.push({
        label: "Open",
        title: "Open",
        options: openClasses.map((cls) => ({
          label: cls.name,
          value: cls.id,
        })),
      });
    }

    return options;
  }, [classes]);

  const onChange = () => {
    form.validateFields().then((values) => {
      setFilters((filter) => ({
        ...filter,
        classTypes: values.class_types || [],
        customerIds: values.customer_ids || [],
        instructorIds: values.staff_ids || [],
        classIds: values.class_ids || [],
      }));
    });
  };

  const onClassTypesChange = (selectedTypes: ClassCategory[]) => {
    const nextClassTypes =
      selectedTypes.length > 0 ? selectedTypes : DEFAULT_CLASS_TYPES;
    form.setFieldsValue({ class_types: nextClassTypes, class_ids: [] });
    setFilters((filter) => ({
      ...filter,
      classTypes: nextClassTypes,
      classIds: [],
      customerIds: form.getFieldValue("customer_ids") || [],
      instructorIds: form.getFieldValue("staff_ids") || [],
    }));
  };

  const onReset = () => {
    setFilters((prev) => ({
      ...prev,
      customerIds: [],
      instructorIds: [],
      classIds: [],
      classTypes: DEFAULT_CLASS_TYPES,
    }));
    form.setFieldsValue({
      class_types: DEFAULT_CLASS_TYPES,
      customer_ids: [],
      staff_ids: [],
      class_ids: [],
    });
  };

  return (
    <Form
      form={form}
      className="schedule-filter"
      name="advancedFilter"
      layout="vertical"
      style={{ width: "100%" }}
      initialValues={{
        class_types: initialClassTypes,
        class_ids: initialClassIds,
        staff_ids: initialStaffIds,
        customer_ids: initialCustomerIds,
      }}
    >
      <Flex vertical>
        <Form.Item
          name="class_types"
          label={<strong>{translate("class")}</strong>}
        >
          <Select
            mode="multiple"
            allowClear
            options={Object.values(ClassCategory).map((value) => ({
              label: capitalizeFirstLetter(value),
              value,
            }))}
            onChange={onClassTypesChange}
            maxTagCount="responsive"
          />
        </Form.Item>
        <Form.Item
          name="class_ids"
          label={<strong>{translate("label.class_name")}</strong>}
        >
          <Select
            mode="multiple"
            allowClear
            loading={classLoading}
            options={classOptions}
            onChange={onChange}
            maxTagCount="responsive"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="customer_ids"
          label={<strong>{translate("customer")}</strong>}
        >
          <Select
            mode="multiple"
            allowClear
            loading={customerLoading}
            options={customerOptions}
            onChange={onChange}
            maxTagCount="responsive"
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="staff_ids"
          label={<strong>{translate("instructor")}</strong>}
        >
          <Select
            mode="multiple"
            allowClear
            loading={staffLoading}
            options={staffOptions}
            onChange={onChange}
            maxTagCount="responsive"
            showSearch
            optionFilterProp="name"
          />
        </Form.Item>
        <div style={{ marginTop: 8 }}>
          <ResetFilterButton
            block
            active={
              initialStaffIds.length > 0 ||
              initialCustomerIds.length > 0 ||
              initialClassIds.length > 0 ||
              initialClassTypes.length !== 2
            }
            onReset={onReset}
          />
        </div>
      </Flex>
    </Form>
  );
});

ScheduleFilter.displayName = "AdvancedFilter";
